import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { RescueRowDto } from '@coachrex/shared';
import { useMe, useOverview, useRescue } from '../lib/api';
import { useRepFilter } from '../lib/repFilter';
import { money, scoreColor } from '../lib/format';
import { useWatching } from '../lib/watching';
import { ErrorBox, RepFilter, Skeleton } from '../components/ui';
import { RescueRow } from '../components/RescueRow';
import { StatusModal } from '../components/StatusModal';
import './rescue.css';
import './revive.css';

/** Handoff screen 01. */
export function Overview() {
  const rep = useRepFilter();
  const { data, error, isLoading } = useOverview(rep.value);
  const rescue = useRescue();
  const me = useMe();
  const { watching, watch } = useWatching(rescue.data?.rows);
  const [editing, setEditing] = useState<RescueRowDto | null>(null);
  const tz = me.data?.company.timeZone;
  const hour = Number(new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: tz }).format(new Date()));
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  if (error) {
    return (
      <div className="page">
        <ErrorBox error={error} />
      </div>
    );
  }
  const currency = data?.currency ?? 'USD';
  const series = data?.scoreSeries ?? [];
  const scored = series.filter((s) => s.score != null);
  const firstScore = scored[0]?.score;
  const lastScore = scored[scored.length - 1]?.score;

  return (
    <div className="page">
      <div className="ov-head">
        <div className="page-head__titles">
          <span className="ov-hello">
            {greeting}
            {data ? `, ${data.greetingName}` : ''}
          </span>
          {isLoading || !data ? (
            <Skeleton height={36} width={360} />
          ) : (
            <h1 className="h1">{data.needReply ? `${data.needReply} lead${data.needReply === 1 ? '' : 's'} need${data.needReply === 1 ? 's' : ''} a reply today` : 'All caught up'}</h1>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {data && data.openCount > 5 && <span className="card-meta" style={{ maxWidth: 170 }}>Showing the top 5 of {data.openCount}, best chance first</span>}
          <RepFilter reps={data?.reps ?? []} />
          <Link to="/rescue" className="btn btn--primary">
            Open rescue queue
          </Link>
        </div>
      </div>

      <section className="card card--list">
        <div className="list-head rescue-grid">
          <span>Priority</span>
          <span>Lead</span>
          <span>Why it’s at risk</span>
          <span style={{ textAlign: 'right' }}>Value</span>
          <span style={{ textAlign: 'right' }}>Since</span>
          <span />
        </div>
        {isLoading || !data ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={52} />
            ))}
          </div>
        ) : data.top.length === 0 ? (
          <div className="list-empty">No open rescue items. Coach Lex flags leads here the moment a call is missed or a message waits too long.</div>
        ) : (
          data.top.map((r) => <RescueRow key={r.id} r={r} currency={currency} watching={watching.has(r.id)} onOpenCrm={watch} onUpdate={setEditing} />)
        )}
      </section>

      {data && data.revive.count > 0 && (
        <Link to="/revive" className="revive-banner">
          <span>
            <b>
              {data.revive.count.toLocaleString()} good lead{data.revive.count === 1 ? '' : 's'} went quiet
            </b>{' '}
            without booking{data.revive.value ? ` · ${money(data.revive.value, currency)} in pipeline` : ''}. Graded A–C, nobody in touch for 2+ weeks.
          </span>
          <span className="btn btn--secondary btn--sm">Open Revive</span>
        </Link>
      )}

      <div className="ov-cards">
        <section className="card stack-14">
          <div className="kpis">
            <div className="stat-card">
              <span className="stat-card__label">Recovered this week</span>
              <span className="stat-card__value">{data ? money(data.recoveredWeek, currency) : '—'}</span>
              <span className="card-meta">{data ? `${data.recoveredWeekCount} lead${data.recoveredWeekCount === 1 ? '' : 's'}` : ''}</span>
            </div>
            <div className="stat-card">
              <span className="stat-card__label">Avg rep score</span>
              <span className="stat-card__value" style={{ color: data?.avgCallScore != null ? scoreColor(data.avgCallScore) : undefined }}>
                {data?.avgCallScore ?? '—'}
              </span>
              <span className="card-meta">Last 14 days</span>
            </div>
          </div>
          <div className="bars" aria-label="Average rep score per day, last 14 days">
            {series.map((s, i) => (
              <span
                key={s.day}
                className={s.score == null ? 'is-empty' : i === series.length - 1 ? 'is-last' : undefined}
                style={{ height: `${s.score ?? 4}%` }}
                title={`${s.day}: ${s.score ?? 'no scored calls'}`}
              />
            ))}
          </div>
          <span className="card-meta">
            {firstScore != null && lastScore != null && scored.length > 1 ? (
              <span style={{ color: lastScore >= firstScore ? 'var(--score-good)' : 'var(--score-bad)', fontWeight: 600 }}>
                {firstScore} → {lastScore}
              </span>
            ) : (
              'Scores appear once AI analysis is on.'
            )}
          </span>
        </section>

        <section className="card stack-10">
          <div className="card-head">
            <span className="card-title">Leaderboard</span>
            <span className="card-meta">Rep score, 30 days</span>
          </div>
          {data?.leaderboard.length ? (
            data.leaderboard.map((b, i) => (
              <div key={b.rep.id} className="mini-row">
                <span style={{ color: 'var(--faint)', fontWeight: 600 }}>{i + 1}</span>
                <span>
                  {b.rep.name} {b.calls != null && <span className="card-meta">· {b.calls} calls</span>}
                </span>
                <span style={{ fontWeight: 600, color: b.score != null ? scoreColor(b.score) : 'var(--faint)' }}>{b.score ?? '—'}</span>
              </div>
            ))
          ) : (
            <span className="card-meta">No answered calls in the last 30 days.</span>
          )}
        </section>

        <section className="card stack-10">
          <div className="card-head">
            <span className="card-title">Where bookings come from</span>
            <Link to="/lead-sources" style={{ fontSize: 12.5 }}>
              Lead sources
            </Link>
          </div>
          {data?.sources.length ? (
            data.sources.map((s) => (
              <div key={s.source} className="mini-row" style={{ gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.source}</span>
                <span className="card-meta">
                  <b style={{ color: 'var(--ink)' }}>{s.booked}</b> booked of {s.leads}
                </span>
              </div>
            ))
          ) : (
            <span className="card-meta">No leads in the last 90 days.</span>
          )}
        </section>
      </div>
      {editing && <StatusModal item={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
